// Pure validation + payload building for the Record Replacement wizard.
// Extracted so the gating logic is unit-testable without mounting the page
// (see CLAUDE.md "test pure logic" convention).

const REMARKS_MAX = 1000;

// Enforce required documents: populate errors.documents so each missing required
// slot shows an inline error and submit is gated. Mirrors the backend
// REQUIRE_DOCUMENTS flag (battery_components_service.py). Set to false only for
// local testing where the document-upload API is unavailable.
const REQUIRE_DOCUMENTS = true;

// Mirrors MIN_SEARCH_LENGTH in pack_master_db_helper.py: the backend returns [] below
// this, so querying would be a wasted round-trip.
export const MIN_SERIAL_SEARCH_LENGTH = 2;

// How long the serial box must be idle before the registry is asked about what was
// typed. DeviceSearchInput has NO debounce of its own -- its suggestions effect runs on
// every distinct value -- so any caller that resolves a typed serial has to supply one
// or it issues a read per keystroke. Shared so the two screens cannot drift apart on it.
export const SERIAL_LOOKUP_DEBOUNCE_MS = 350;

const DEFAULT_SERIAL_SUGGESTION_LIMIT = 10;

// Pack-registry UI behaviour from orgMeta components.replacement.packRegistry.
// selfHealEnabled is deliberately NOT read here: it gates a backend write and is resolved
// server-side, so a UI copy could only ever be misleading.
export const getPackRegistryConfig = (config = {}) => {
    const packRegistry = config?.packRegistry || {};
    return {
        warnWhenUnregistered: packRegistry.warnWhenUnregistered !== false,
        suggestionLimit:
            packRegistry.suggestionLimit || DEFAULT_SERIAL_SUGGESTION_LIMIT,
    };
};

// packConfigMaster.manufacturingDate is a tz-less DATE column, but the list endpoint
// serialises every date through DATE_TIME_FORMAT and hands back "YYYY-MM-DD 00:00:00".
// The form state, the review screen and the create payload all want the bare calendar
// day, so trim the phantom time rather than let a datetime string reach the backend.
// Deliberately NOT a timezone conversion: shifting a manufacturing date by the viewer's
// offset would move it a day for anyone west of UTC.
export const toCalendarDay = (value) => {
    if (!value) return null;
    const match = String(value).match(/^\d{4}-\d{2}-\d{2}/);
    return match ? match[0] : null;
};

// The registry auto-fill binds a manufacturing date to the exact serial it was fetched
// for. Both the "is this date still the registry's answer" checks go through here so the
// lock and the release can never disagree about what counts as the same serial.
export const matchesAutoFilledSerial = (autoFilledSerial, serial) =>
    !!autoFilledSerial && autoFilledSerial === (serial || "").trim();

// True when the manufacturing-date field is showing a registry value for the serial
// currently in the form — the state in which it is read-only. A hand-typed date has no
// auto-filled serial behind it and stays editable.
export const isRegistryDateLocked = (autoFilledSerial, serial, manufacturingDate) =>
    !!manufacturingDate && matchesAutoFilledSerial(autoFilledSerial, serial);

const nonEmptyCustoms = (customProblems = []) =>
    customProblems.map((c) => (c || "").trim()).filter(Boolean);

// Whether a documentType must be attached. Required unless orgMeta marks it
// optional (required === false, e.g. "Other document"); additionally the Testing
// ICAN/MYCAN document is optional when the incoming pack is cannibalised (there is
// no fresh testing data for a cannibalised pack). Mirrors the backend rule in
// battery_components_service._validateDocuments.
export const isDocumentRequired = (docType, incomingCondition) => {
    if (!docType || docType.required === false) return false;
    if (docType.code === "TESTING_ICAN_MYCAN" && incomingCondition === "CANNIBALISED") {
        return false;
    }
    return true;
};

// The request's effective incoming condition, for rules about the SHARED
// documents. Only "CANNIBALISED" when every pack is cannibalised: one set of
// documents covers the whole request, so a single non-cannibalised pack still
// needs testing data. "MIXED" is deliberately not a real condition code — it
// just has to be something isDocumentRequired treats as not-cannibalised.
const effectiveRequestCondition = (packs = []) => {
    const conditions = (packs || [])
        .map((pack) => pack?.incoming?.condition)
        .filter(Boolean);
    if (!conditions.length) return "";
    return conditions.every((c) => c === "CANNIBALISED") ? "CANNIBALISED" : "MIXED";
};

// Whether a shared document must be attached for this request. Delegates to the
// single-pack rule above so the cannibalised exception is defined once.
export const isRequestDocumentRequired = (docType, packs = []) =>
    isDocumentRequired(docType, effectiveRequestCondition(packs));

// A resolved components-tree pack -> one entry in formState.packs.
//
// absPackNo doubles as the React key: it is the stable slot number and a slot can
// only appear once in a request, so no separate counter is needed.
export const createPackFormEntry = (pack = {}) => ({
    key: String(pack.absPackNo),
    absPackNo: pack.absPackNo,
    packConfigID: pack.packConfigID,
    stringLabel: pack.stringLabel || "",
    packLabel: pack.label || "",
    // Whether the slot already has a serial on record. When it does the outgoing
    // serial is read-only; when it does not the technician types it by hand. The
    // install date is tracked separately by installedOnOnRecord below — the two
    // are independent, and keying the date off the serial was the original bug.
    serialAutoFilled: !!pack.serial,
    // Whether the slot had an install date ON RECORD when the form opened. The
    // "Installed on" field is read-only only in that case. Deliberately not derived
    // from the current form value: that would re-lock the field the instant the
    // technician picked a date, leaving no way to correct it.
    installedOnOnRecord: !!pack.installedOn,
    outgoing: {
        serial: pack.serial || "",
        installedOn: pack.installedOn || null,
        warrantyStatus: "",
        // Per pack, and keyed to this pack's own warranty answer.
        observations: [],
        // Whether the serial currently in the field was typed by hand, as opposed to
        // prefilled from the slot or picked from the suggestions dropdown. Only
        // hand-typed serials are format-checked; see isTypedSerialInvalid.
        //
        // Kept in form state rather than in the card so submit is gated, not merely
        // annotated -- the same reason incoming.installedElsewhere lives here. Starts
        // false: a prefilled serial is by definition not typed.
        serialTyped: false,
    },
    incoming: {
        serial: "",
        manufacturingDate: null,
        grade: "",
        condition: "",
        serialTyped: false,
    },
});

// Chip label for a pack card: "String 1 · Pack 3".
export const formatPackChipLabel = (pack = {}) =>
    [pack.stringLabel, pack.packLabel].filter(Boolean).join(" · ");

// Observation options for ONE pack, chosen by that pack's warranty status — the
// same rule the form used before it went multi-pack. Empty until warranty is
// answered, which is what keeps the block hidden until then.
//
// Because the list follows the pack's own warranty, an observation code can never
// contradict the warranty it was recorded against, and the backend's existing
// per-warranty validation stays correct with no relaxation.
export const getObservationOptionsForWarranty = (config = {}, warrantyStatus) => {
    const observations = config?.observations || {};
    if (warrantyStatus === "IN") return observations.inWarranty || [];
    if (warrantyStatus === "OUT") return observations.outOfWarranty || [];
    return [];
};

// Every observation option across both warranty lists, de-duplicated by code.
// Read-only screens see stored codes without knowing which list they came from, so
// label lookup has to span both.
export const getObservationOptions = (config = {}) => {
    const observations = config?.observations || {};
    const byCode = new Map();
    [...(observations.inWarranty || []), ...(observations.outOfWarranty || [])].forEach(
        (option) => {
            if (option?.code && !byCode.has(option.code)) byCode.set(option.code, option);
        }
    );
    return Array.from(byCode.values());
};

// Label for one observation code, looked up across both lists. Used by the review
// and detail screens, which see only stored codes.
export const getObservationLabel = (config = {}, code) => {
    const found = getObservationOptions(config).find((option) => option.code === code);
    return found ? found.label : code;
};

const normalizeSerial = (serial) => (serial || "").trim().toUpperCase();

// A serial may contain capital letters and digits, nothing else.
//
// Strict ASCII on purpose. The character class rejects non-ASCII letters, which is the
// only automated defence against a hazard already present in this data: the client sheet
// carried 36 Cyrillic A/B/E/M across 3 buses, and because MySQL's utf8mb4_0900_ai_ci is
// accent-insensitive, a serial starting "AJBM..." in Cyrillic silently upserts onto a
// DIFFERENT bus's row rather than failing. Nothing downstream catches that, so a
// homoglyph pasted from a spreadsheet has to be stopped at entry.
//
// Every one of the 21,176 imported serials is already strictly alphanumeric, and the
// import's own reject list is the same shape ("NO BATTERY PACK", "STICKER NOT AVAILABLE"
// -- all of them fail on the space), so this blocks nothing legitimate.
export const SERIAL_ALLOWED_PATTERN = /^[A-Z0-9]+$/;

// Applied to what the technician TYPES, so the field can only ever hold the canonical
// form. Serials are case-insensitive throughout (normalizeSerial above already
// upper-cases for duplicate detection, and the DB collations are _ci), so folding case
// here changes no meaning -- it just spares someone typing on a tablet a red error for a
// serial that was already correct.
//
// Note this does NOT strip disallowed characters. Silently deleting what someone typed
// hides the mistake; they need to see the wrong character to know a paste went wrong.
export const toTypedSerial = (value) => (value || "").toUpperCase();

export const INVALID_SERIAL_MESSAGE =
    "Only capital letters and numbers are allowed";

// Whether a serial the technician typed BY HAND is malformed.
//
// Auto-populated values are exempt and must never be passed here. An outgoing serial
// prefilled from the slot, or either serial picked from the suggestions dropdown, is
// already-recorded data: some of it predates this rule and some carries the very
// homoglyphs described above. Validating it would block a technician from recording a
// replacement over a bad row -- work they cannot do anything else about, on a field they
// did not fill in.
//
// Empty is not "invalid" here; it is the separate required-field case, which owns its
// own message.
export const isTypedSerialInvalid = (value) => {
    const trimmed = (value || "").trim();
    if (!trimmed) return false;
    return !SERIAL_ALLOWED_PATTERN.test(trimmed);
};

// Serials repeated across the packs of ONE request, for the given side. A pack
// cannot be installed in two slots at once, and the same pack cannot be removed
// from two slots — the backend rejects both, but only after submit.
// Pack keys whose INCOMING serial is some OTHER pack's OUTGOING serial: the same
// physical pack leaving one slot and arriving in another within one request.
//
// Deliberately ignores a pack whose own outgoing and incoming serial match. That is
// re-installing the same pack into the slot it came from -- a legitimate record of a
// reseat, and one the backend explicitly allows by excluding the slot being replaced
// from its active-serial check.
const findSerialsMovedBetweenPacks = (packs = []) => {
    const outgoingByKey = new Map();
    (packs || []).forEach((pack) => {
        const serial = normalizeSerial(pack?.outgoing?.serial);
        if (serial) outgoingByKey.set(pack?.key, serial);
    });

    const movedKeys = new Set();
    (packs || []).forEach((pack) => {
        const incomingSerial = normalizeSerial(pack?.incoming?.serial);
        if (!incomingSerial) return;
        outgoingByKey.forEach((outgoingSerial, key) => {
            if (key !== pack?.key && outgoingSerial === incomingSerial) {
                movedKeys.add(pack?.key);
            }
        });
    });
    return movedKeys;
};

const findDuplicateSerials = (packs = [], side = "incoming") => {
    const seen = new Set();
    const duplicates = new Set();
    (packs || []).forEach((pack) => {
        const serial = normalizeSerial(pack?.[side]?.serial);
        if (!serial) return;
        if (seen.has(serial)) duplicates.add(serial);
        seen.add(serial);
    });
    return Array.from(duplicates);
};

export const DUPLICATE_SERIAL_MESSAGE =
    "This serial is already used by another pack in this request";

// A serial coming OUT of one slot and INTO another in the same request describes a
// pack being moved between slots. It is a real thing technicians do, but it cannot
// be recorded as one request: the write deactivates the outgoing row and creates
// the incoming one in a single transaction, so the moved pack would have to be
// retired and installed at the same instant. Recorded as two requests -- remove,
// then install -- the history reads correctly and each slot keeps its own timeline.
// Deliberately says "unmatched", not "unregistered": a serial can be perfectly
// valid and still miss the suggestions for being installed elsewhere, and the two
// cases are indistinguishable from the suggestion list alone.
export const UNMATCHED_SERIAL_WARNING =
    "Unmatched serial number - please verify before saving";

export const MISSING_SERIAL_MESSAGE = "Please fill the serial number";

// What a serial field says beneath itself, and in which colour.
//
// An ERROR always outranks the unmatched-serial ADVISORY. The advisory used to win,
// which meant a serial that was both unrecognised and duplicated across two cards
// only ever said "please verify before saving" -- the one message that does not
// explain why the form will not submit. Since a hand-typed serial is unmatched by
// definition, that hid the real reason in precisely the case it mattered.
//
// `error` is either `true` (the field is empty) or the message itself, which is how
// validateRecordReplacement reports the duplicate and moved-between-slots cases.
export const getSerialFieldMessage = ({
    error = null,
    isInvalidFormat = false,
    isUnmatched = false,
    fallback = " ",
} = {}) => {
    // Outranks everything, including the duplicate and installed-elsewhere messages. A
    // malformed serial is the more basic problem and the only one whose fix is obvious
    // from the field alone; telling someone their typo duplicates another card's typo
    // sends them to fix the wrong card.
    if (isInvalidFormat)
        return { text: INVALID_SERIAL_MESSAGE, isError: true, isWarning: false };
    if (typeof error === "string") return { text: error, isError: true, isWarning: false };
    if (error) return { text: MISSING_SERIAL_MESSAGE, isError: true, isWarning: false };
    if (isUnmatched)
        return { text: UNMATCHED_SERIAL_WARNING, isError: false, isWarning: true };
    return { text: fallback, isError: false, isWarning: false };
};

// Where to take the user when a submit fails validation, as an anchor name the page
// resolves to a DOM node. Returns null when nothing is wrong.
//
// The order below is the order the sections appear on screen, so the user is always
// sent to the FIRST thing that needs attention rather than an arbitrary one. Pure so
// the ordering is unit-tested: getting it wrong sends someone to the bottom of a long
// form to fix a field near the top.
export const FORM_ANCHORS = {
    problemsReported: "problemsReported",
    packs: "packs",
    documents: "documents",
    timeline: "timeline",
    remarks: "remarks",
};

export const getFirstErrorAnchor = (errors = {}, packs = []) => {
    if (!errors || typeof errors !== "object") return null;

    if (errors.problemsReported) return FORM_ANCHORS.problemsReported;

    // A pack row: identified by its own key, since each card is its own anchor.
    // Walked in the order the cards are rendered, not the order of the error map,
    // whose key order is not meaningful.
    const erroredPack = (packs || []).find((pack) => errors.packs?.[pack?.key]);
    if (erroredPack) return `pack:${erroredPack.key}`;

    // "No packs at all" belongs where the packs would be.
    if (errors.noPacks) return FORM_ANCHORS.packs;

    if (errors.documents && Object.keys(errors.documents).length > 0) {
        return FORM_ANCHORS.documents;
    }

    if (errors.busHeldUpAt || errors.workStartAt || errors.workEndAt) {
        return FORM_ANCHORS.timeline;
    }

    if (errors.remarks || errors.acknowledged) return FORM_ANCHORS.remarks;

    return null;
};

// A serial the registry says is currently installed on some other battery. The
// suggestions deliberately OFFER these: hiding them left a technician holding a real
// pack unable to tell an unknown serial from a taken one, so they typed it by hand and
// met a 409 after filling the whole form. Offered, then refused with a reason.
//
// Whether a registry row means "this pack is already installed elsewhere", and how to
// name the vehicle it sits on. Returns null when the serial is free to install here.
//
// Free covers FOUR cases, and they are not the same thing:
//   - the registry has no row at all (a serial the technician is registering as they go)
//   - the row exists but maps to no vehicle (inventory, sitting on a shelf)
//   - the row maps to THIS battery -- the pack in this very slot, being re-seated, which
//     the backend also permits by excluding the slot under replacement from its check
//   - the row was FILTERED OUT for this caller. The lookup behind this goes through the
//     scope-filtered list endpoint, which drops a pack mapped to a vehicle the caller
//     cannot see -- row arrives empty, and this reads as free.
//
// That last case is a real gap, not a privacy feature: a technician outside the holding
// depot gets NO warning here and still meets the backend 409 on submit. Closing it needs
// a scope-exempt existence check ("is this serial installed anywhere?" without saying
// where), which does not exist yet. The backend remains the backstop.
//
// vehicleLabel is whatever identifies the vehicle to a human, falling back through the
// registry's own columns; null only when a VISIBLE row carries neither.
export const getIncomingSerialConflict = (registryRow, currentBatteryID) => {
    const row = registryRow || {};
    const installedOnBatteryID = row.batteryID;
    if (!installedOnBatteryID) return null;
    if (String(installedOnBatteryID) === String(currentBatteryID)) return null;

    return {
        batteryID: installedOnBatteryID,
        vehicleLabel: row.licensePlateNo || row.vin || null,
    };
};

// assetLabel names the thing the pack sits in, supplied by the caller from org meta:
// orgs that run buses and orgs that run other vehicles do not share one noun.
export const formatSerialInstalledElsewhere = (vehicleLabel, assetLabel = "vehicle") =>
    vehicleLabel
        ? `This serial is installed on ${vehicleLabel}. Remove it there first, or use a different pack`
        : `This serial is installed on another ${assetLabel}. Remove it there first, or use a different pack`;

export const SERIAL_MOVED_BETWEEN_PACKS_MESSAGE =
    "This pack is being removed from another slot in this request. Record the move as a separate replacement";

// Timeline ordering check, shared by validateRecordReplacement (submit gate) and
// TimelineSection (reactive inline display on selection). Returns a map of
// { workStartAt?, workEndAt? } -> inline error message, only for values that are
// present, parseable, and out of order (Non-operational <= Work start <= Work end).
export const getTimelineOrderErrors = (timeline = {}) => {
    const toTime = (v) => {
        if (!v) return null;
        const t = new Date(v).getTime();
        return Number.isNaN(t) ? null : t;
    };
    const nonOpTime = toTime(timeline.busHeldUpAt);
    const workStartTime = toTime(timeline.workStartAt);
    const workEndTime = toTime(timeline.workEndAt);

    const orderErrors = {};
    if (nonOpTime !== null && workStartTime !== null && workStartTime < nonOpTime) {
        orderErrors.workStartAt = "Work start must be after the Issue reported date";
    }
    if (workStartTime !== null && workEndTime !== null && workEndTime < workStartTime) {
        orderErrors.workEndAt = "Work end must be after the Work start date";
    }
    return orderErrors;
};

// Returns { errors, isValid }.
//
// `errors` is keyed for inline display and split by scope: request-level keys sit
// at the top, and per-pack keys live under errors.packs[pack.key] so an invalid
// field can be highlighted on the card it belongs to rather than somewhere on a
// page with six identical-looking cards.
export const validateRecordReplacement = (formState = {}, config = {}) => {
    const {
        problemsReported = [],
        customProblems = [],
        packs = [],
        timeline = {},
        remarks = "",
        acknowledged = false,
        documents = {},
    } = formState;

    const errors = { packs: {} };

    // ---- request-level ----

    // Problem reported: at least one predefined or non-empty custom.
    if (!problemsReported.length && !nonEmptyCustoms(customProblems).length) {
        errors.problemsReported = true;
    }

    if (!packs.length) errors.noPacks = true;

    // ---- per pack ----
    const duplicateIncoming = new Set(findDuplicateSerials(packs, "incoming"));
    const duplicateOutgoing = new Set(findDuplicateSerials(packs, "outgoing"));
    const movedBetweenPacks = findSerialsMovedBetweenPacks(packs);

    packs.forEach((pack) => {
        const outgoing = pack?.outgoing || {};
        const incoming = pack?.incoming || {};
        const packErrors = {};

        if (!(outgoing.serial || "").trim()) {
            packErrors.outgoingSerial = true;
        } else if (outgoing.serialTyped && isTypedSerialInvalid(outgoing.serial)) {
            packErrors.outgoingSerial = INVALID_SERIAL_MESSAGE;
        } else if (duplicateOutgoing.has(normalizeSerial(outgoing.serial))) {
            packErrors.outgoingSerial = DUPLICATE_SERIAL_MESSAGE;
        }

        if (!outgoing.warrantyStatus) packErrors.warranty = true;

        // Observation is required, but only once this pack's warranty is answered:
        // its options are derived from that answer, so the block is not on screen
        // before it. No hole — warranty is itself required, so the pack is invalid
        // either way until both are filled.
        if (outgoing.warrantyStatus && !(outgoing.observations || []).length) {
            packErrors.observations = true;
        }

        if (!(incoming.serial || "").trim()) {
            packErrors.incomingSerial = true;
        } else if (incoming.serialTyped && isTypedSerialInvalid(incoming.serial)) {
            packErrors.incomingSerial = INVALID_SERIAL_MESSAGE;
        } else if (duplicateIncoming.has(normalizeSerial(incoming.serial))) {
            // Reported ahead of the moved-between-slots case: two cards naming the
            // same new pack is the simpler contradiction, and fixing it often
            // resolves the other one too.
            packErrors.incomingSerial = DUPLICATE_SERIAL_MESSAGE;
        } else if (movedBetweenPacks.has(pack.key)) {
            packErrors.incomingSerial = SERIAL_MOVED_BETWEEN_PACKS_MESSAGE;
        } else if (incoming.installedElsewhere) {
            // Recorded on the pack by the card when its registry lookup came back
            // mapped to another battery. Kept in form state rather than left as card
            // state so submit is blocked, not merely annotated.
            packErrors.incomingSerial = formatSerialInstalledElsewhere(
                incoming.installedElsewhere.vehicleLabel
            );
        }

        if (!incoming.manufacturingDate) packErrors.manufacturingDate = true;
        if (!incoming.grade) packErrors.grade = true;
        if (!incoming.condition) packErrors.condition = true;

        if (Object.keys(packErrors).length) errors.packs[pack.key] = packErrors;
    });

    // ---- timeline: all three required ----
    if (!timeline.busHeldUpAt) errors.busHeldUpAt = true;
    if (!timeline.workStartAt) errors.workStartAt = true;
    if (!timeline.workEndAt) errors.workEndAt = true;

    // Timeline ordering: Non-operational <= Work start <= Work end. Shared with
    // TimelineSection so the same message shows reactively on selection.
    const orderErrors = getTimelineOrderErrors(timeline);
    if (!errors.workStartAt && orderErrors.workStartAt) errors.workStartAt = orderErrors.workStartAt;
    if (!errors.workEndAt && orderErrors.workEndAt) errors.workEndAt = orderErrors.workEndAt;

    // ---- shared documents ----
    // One set of documents covers the request, so the cannibalised exception for
    // Testing ICAN/MYCAN is evaluated across all packs, not one.
    if (REQUIRE_DOCUMENTS) {
        const missingDocs = (config.documentTypes || [])
            .filter((d) => isRequestDocumentRequired(d, packs))
            .filter((d) => !documents[d.code]?.documentID)
            .map((d) => d.code);
        if (missingDocs.length) errors.documents = missingDocs;
    }

    // ---- remarks + acknowledgment ----
    const trimmedRemarks = (remarks || "").trim();
    if (!trimmedRemarks || trimmedRemarks.length > REMARKS_MAX) errors.remarks = true;
    if (!acknowledged) errors.acknowledged = true;

    // errors.packs is always present (an empty map), so it cannot be counted as an
    // error itself.
    const requestErrorCount = Object.keys(errors).filter((k) => k !== "packs").length;
    const packErrorCount = Object.keys(errors.packs).length;

    return { errors, isValid: requestErrorCount === 0 && packErrorCount === 0 };
};

// Assemble the POST /api/service-records body (section 3.5b), one
// packReplacementDetails entry per pack.
//
// reasons are answered once for the request and written onto every pack: it is a
// per-pack column in the DB, and replicating it keeps a stored row self-describing
// rather than meaningless without its parent. observations are NOT replicated —
// they are answered per pack, against that pack's own warranty status.
export const buildCreatePayload = ({ formState = {}, batteryID, snapshotToken }) => {
    const {
        problemsReported = [],
        customProblems = [],
        packs = [],
        timeline = {},
        remarks = "",
        documents = {},
    } = formState;

    const reasons = [...problemsReported, ...nonEmptyCustoms(customProblems)];
    const documentIDs = Object.values(documents)
        .filter(Boolean)
        .map((d) => d.documentID)
        .filter(Boolean);

    // The record-level warrantyStatus is on its way out: warranty now lives on each
    // pack, and packs may legitimately disagree. Still sent WHEN THEY AGREE, for
    // readers that predate the per-pack column; omitted when they do not, because
    // there is no single answer for the request and picking one would be a lie.
    // Do not reintroduce a same-warranty check here -- mixed is valid.
    const warrantyStatuses = Array.from(
        new Set(packs.map((pack) => pack?.outgoing?.warrantyStatus).filter(Boolean))
    );

    return {
        batteryID,
        serviceType: "PACK_REPLACEMENT",
        snapshotToken,
        documentIDs,
        remarks: (remarks || "").trim(),
        approvalAcknowledged: !!formState.acknowledged,
        serviceDetails: {
            ...(warrantyStatuses.length === 1 && { warrantyStatus: warrantyStatuses[0] }),
            busHeldUpAt: timeline.busHeldUpAt,
            workStartAt: timeline.workStartAt,
            workEndAt: timeline.workEndAt,
        },
        packReplacementDetails: packs.map((pack) => ({
            packConfigID: pack.packConfigID,
            outgoingSerial: (pack?.outgoing?.serial || "").trim(),
            incomingSerial: (pack?.incoming?.serial || "").trim(),
            incomingCondition: pack?.incoming?.condition,
            incomingManufacturingDate: pack?.incoming?.manufacturingDate,
            packGrade: pack?.incoming?.grade,
            warrantyStatus: pack?.outgoing?.warrantyStatus,
            observations: pack?.outgoing?.observations || [],
            // Write-once: sent only when the slot had NO install date on record, so
            // a stored date can never be overwritten from here. The backend now
            // persists this onto the outgoing packConfig row (it is no longer inert).
            // Still a user-zone calendar day at this point — the page converts it to
            // UTC at the submit boundary, since packConfig.installedOn is a UTC
            // DATETIME and the bare local day would read back a day early west of UTC.
            ...(pack?.outgoing?.installedOn &&
                !pack?.installedOnOnRecord && {
                    outgoingInstalledOn: pack.outgoing.installedOn,
                }),
            reasons,
        })),
    };
};
